import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  FlatList,
  View,
  TouchableOpacity,
  Pressable,
  Platform,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';

import Header from '../../components/elements/Header';
import TabHeader from '../../components/elements/TabHeader';
import Text from '../../components/elements/Text';
import FavoriteResourceCard from '../../components/modules/FavoriteResourceCard';
import ResourceCard from '../../components/modules/ResourceCard';
import SearchFilterView from '../../components/modules/SearchFilterView';
import PageView from '../../components/elements/PageView';
import SuggestResources from '../SuggestResources';
import {
  useNavigate,
  useOutletContext,
} from '../../utils/mockedWebLibs/react-router-dom';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {VIEW_TYPE_RESOURCES} from '../../utils/Constants';
import {fetchApi, PAGE_SIZE} from '../../utils/ApiHelper';
import {
  HTTP_METHODS,
  RESOURCES,
  RESOURCE_CATEGORY,
} from '../../utils/URLConstants';
import {DLog, objToQueryString} from '../../utils/HelperMethods';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import CommonStyles from '../../utils/CommonStyles';
import IconButton from '../../components/elements/IconButton';
import {FlatGrid} from 'react-native-super-grid';
import {tID} from '../../utils/TestIdHelper';
import {EmptyFeed, HeartIcon, LeftArrowIcon} from '../../utils/SvgIcons';

export const HeaderText = ({viewType, setViewType}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return viewType === VIEW_TYPE_RESOURCES.Resources ? (
    <View
      testID={tID('Resources-View-HeaderContainer')}
      style={CommonStyles.flex_row}>
      <Text
        testID={tID('Resources-Text-Resources')}
        variation="Headline4SemiBold"
        color={colors.textPrimary}>
        Resources
      </Text>
    </View>
  ) : (
    <View
      testID={tID('Resources-View-HeaderContainer')}
      style={styles.headerTextContainer}>
      <Text
        testID={tID('Resources-Text-Resources')}
        variation="Text2Medium"
        color={colors.textDisabled}
        onPress={() => setViewType(VIEW_TYPE_RESOURCES.Resources)}>
        Resources
      </Text>
      <LeftArrowIcon style={CommonStyles.mh_12} />
      <Text
        testID={tID('Resources-Text-MyFavorites')}
        variation="Text2SemiBold"
        color={colors.textHeading}>
        My Favorites
      </Text>
    </View>
  );
};

function Resources({navigation}) {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const [viewType, setViewType] = useState(VIEW_TYPE_RESOURCES.Resources); //default view will be resources
  const [resources, setResources] = useState([]);
  const [recommendedResources, setRecommendedResources] = useState([]);
  const [resourceCategories, setResourceCategories] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFilterEnabled, setSearchFilterEnabled] = useState(false);
  const [itemCount, setItemCount] = useState(0);

  const [headerComponentVisibility, setHeaderComponentVisibility] =
    useState(true); //default view is resources with header component hence this is true

  const searchTimeout = useRef(null);
  const flatlist = useRef(null);
  const didLoad = useRef(false);
  const recepiesTagId = useRef(null);
  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);
  const navigate = useNavigate();

  const {setHeaderName} = useOutletContext();

  useLayoutEffect(() => {
    if (setHeaderName)
      setHeaderName(
        <HeaderText viewType={viewType} setViewType={setViewType} />,
      );
  }, [setHeaderName, viewType]);

  useEffect(() => {
    if (viewType === VIEW_TYPE_RESOURCES.Favorites) {
      setHeaderComponentVisibility(false);
    } else {
      setHeaderComponentVisibility(true);
    }
  }, [viewType]);

  useEffect(() => {
    if (viewType == VIEW_TYPE_RESOURCES.Favorites) {
      const params = {
        type: 'favourites',
        pageNo: 1,
        size: PAGE_SIZE,
      };

      setCurrentPage(1);
      setResources([]);
      getResources(params);
    } else {
      if (didLoad.current) {
        const params = {
          type: 'all',
          pageNo: 1,
          size: PAGE_SIZE,
        };

        setCurrentPage(1);
        setResources([]);
        getResources(params);
      } else {
        getResourceCategory();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch helpers are re-created each render; refetch is intentionally keyed to viewType changes only
  }, [viewType]);

  useEffect(() => {
    if (didLoad.current) {
      setSearchFilterBool(true);

      const params = {
        type: viewType === VIEW_TYPE_RESOURCES.Favorites ? 'favourites' : 'all',
        pageNo: 1,
        size: PAGE_SIZE,
      };

      getResources(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- helpers are re-created each render and viewType is read as a snapshot; refetch is intentionally keyed to filter/tag changes only
  }, [selectedFilters, selectedTags]);

  useEffect(() => {
    if (didLoad.current) {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
        searchTimeout.current = null;
      }

      searchTimeout.current = setTimeout(() => {
        const params = {
          type:
            viewType === VIEW_TYPE_RESOURCES.Favorites ? 'favourites' : 'all',
          pageNo: 1,
          size: PAGE_SIZE,
        };
        setSearchFilterBool(searchTerm);
        getResources(params);
      }, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounced search fetch is intentionally keyed to searchTerm only; helpers are re-created each render
  }, [searchTerm]);

  const setSearchFilterBool = searchText => {
    if (searchText.length > 0 || selectedFilters.length > 0) {
      if (!searchFilterEnabled) {
        setSearchFilterEnabled(true);
      }
    } else {
      if (searchFilterEnabled) {
        setSearchFilterEnabled(false);
      }
    }
  };

  const onRefreshList = () => {
    const params = {
      type: viewType === VIEW_TYPE_RESOURCES.Favorites ? 'favourites' : 'all',
      pageNo: 1,
      size: PAGE_SIZE,
    };

    setCurrentPage(1);
    setResources([]);
    getResources(params);
  };

  const getResourceCategory = () => {
    lpc?.setVisible(true);
    fetchApi(HTTP_METHODS.GET, RESOURCE_CATEGORY)
      .then(res => {
        if (res.status == 200) {
          const data = res.data.data.data;
          const allTags = [{name: 'All'}, ...data];
          data.every(i => {
            if (i.name === 'Recipes') {
              recepiesTagId.current = i.id;
            }
            return true;
          });
          setResourceCategories(allTags);
          setSelectedTags([allTags[0]]);
          const params = {
            type:
              viewType === VIEW_TYPE_RESOURCES.Favorites ? 'favourites' : 'all',
            pageNo: currentPage,
            size: PAGE_SIZE,
          };
          getResources(params);
        } else {
          lpc?.setVisible(false);
        }
      })
      .catch(e => {
        // Dlog(e);
        lpc?.setVisible(false);
        esc?.showError(e);
      });
  };

  const getResources = params => {
    if (!lpc?.visible) {
      lpc?.setVisibleWithTimeout(true);
    }

    let allParams = params;
    if (searchTerm.length > 0) {
      allParams.search = searchTerm;
    }

    let queryParams = objToQueryString(allParams);
    if (selectedFilters.length > 0) {
      if (!selectedFilters.includes(resourceCategories[0])) {
        selectedFilters.forEach(i => {
          queryParams = queryParams + '&category=' + encodeURIComponent(i.id);
        });
      }
    }

    if (selectedFilters.length == 0) {
      if (!selectedTags.includes(resourceCategories[0])) {
        selectedTags.forEach(i => {
          queryParams = queryParams + '&category=' + encodeURIComponent(i.id);
        });
      }
    }

    fetchApi(HTTP_METHODS.GET, RESOURCES + '?' + queryParams)
      .then(res => {
        // Dlog(res);
        lpc.setVisibleWithTimeout(false);
        if (res.status == 200) {
          setResources(res.data.data.rows);
          setItemCount(res.data.data.count);
          if (res.data.data.count > 0 && res.data.data.count % PAGE_SIZE == 0) {
            setPageCount(parseInt(res.data.data.count / PAGE_SIZE));
          } else {
            setPageCount(parseInt(res.data.data.count / PAGE_SIZE) + 1);
          }
          didLoad.current = true;
        }
      })
      .catch(e => {
        // Dlog(e);
        lpc?.setVisibleWithTimeout(false);
        esc?.showError(e);
      });
  };

  const onChangePage = page => {
    setCurrentPage(page);
    const params = {
      type: viewType === VIEW_TYPE_RESOURCES.Favorites ? 'favourites' : 'all',
      pageNo: page,
      size: PAGE_SIZE,
    };

    getResources(params);
    if (flatlist.current) {
      flatlist.current.scrollToOffset({animated: true, offset: 0});
    }
  };

  const onChangeSearchTerm = text => {
    setSearchTerm(text);
  };

  const onPressCategory = item => {
    setCurrentPage(1);
    let selected = [...selectedTags];
    if (selected.length == 1 && selected[0] == resourceCategories[0]) {
      selected = [];
    }
    let idx = selected.indexOf(item);
    if (idx == -1) {
      selected.push(item);
    } else {
      const _ = selected.splice(idx, 1);
      if (selected.length == 0) {
        selected = [resourceCategories[0]];
      }
    }
    if (item == resourceCategories[0] && idx != -1) {
      return;
    }
    if (item == resourceCategories[0] && idx == -1) {
      selected = [resourceCategories[0]];
    }
    setSelectedTags(selected);
  };

  const renderSearchFilterHeaderText = () => {
    if (selectedFilters.length > 0) {
      return (
        <Text variation="Subtitle1SemiBold" color={colors.textHeading}>
          All Results{' '}
          <Text
            testID={tID('Resources-Text-AllResultsCount')}
            variation="Subtitle1Regular"
            color={colors.textHeading}>
            ({itemCount})
          </Text>
        </Text>
      );
    }

    return (
      <Text variation="Subtitle1SemiBold" color={colors.textHeading}>
        Search Results{' '}
        <Text
          testID={tID('Resources-Text-SearchResultsCount')}
          variation="Subtitle1Regular"
          color={colors.textHeading}>
          ({itemCount})
        </Text>
      </Text>
    );
  };

  const renderHeaderComponent = () => {
    return headerComponentVisibility ? (
      <>
        {!searchFilterEnabled && (
          <SuggestResources title={'Recommended Resources'} isSeeAll={false} />
        )}
        {!searchFilterEnabled && (
          <Text
            testID={tID('Resources-Text-AllResources')}
            variation="Headline4SemiBold"
            color={colors.textHeading}
            style={CommonStyles.full_flex}>
            All Resources
          </Text>
        )}
        {searchFilterEnabled && renderSearchFilterHeaderText()}
        {selectedFilters.length == 0 && renderTagList()}
      </>
    ) : (
      <>
        {searchFilterEnabled && renderSearchFilterHeaderText()}
        {selectedFilters.length == 0 && renderTagList()}
      </>
    );
  };

  const renderTagList = () => {
    return (
      <FlatList
        testID={tID('Resources-FlatList-Tags')}
        horizontal
        showsHorizontalScrollIndicator={false}
        data={resourceCategories}
        renderItem={renderTags}
        keyExtractor={(item, index) => index}
        showsVerticalScrollIndicator={false}
        style={styles.marginTop16}
        extraData={selectedTags}
      />
    );
  };

  const wrapWithPressable = (card, data) => {
    return (
      <Pressable
        testID={tID('Resources-Pressable-Card')}
        onPress={() => {
          if (Platform.OS === 'web') {
            navigate('/resource-details', {
              state: {
                data,
              },
            });
          } else {
            navigation.navigate('ViewResource', {
              data: data,
              refreshMainList: () => {
                const params = {
                  type:
                    viewType === VIEW_TYPE_RESOURCES.Favorites
                      ? 'favourites'
                      : 'all',
                  pageNo: currentPage,
                  size: PAGE_SIZE,
                };
                getResources(params);
              },
            });
          }
        }}>
        {card}
      </Pressable>
    );
  };

  const renderResourceItem = ({item, index}) => {
    if (viewType === VIEW_TYPE_RESOURCES.Favorites) {
      return wrapWithPressable(
        <FavoriteResourceCard
          item={item}
          index={index}
          refreshMainList={() => {
            const params = {
              type:
                viewType === VIEW_TYPE_RESOURCES.Favorites
                  ? 'favourites'
                  : 'all',
              pageNo: currentPage,
              size: PAGE_SIZE,
            };
            getResources(params);
          }}
        />,
        item,
      );
    }
    return wrapWithPressable(<ResourceCard item={item} index={index} />, item);
  };

  const renderTags = ({item, index}) => {
    return (
      <TouchableOpacity
        testID={tID(`Resources-TouchableOpacity-TagTouch${index}`)}
        onPress={() => onPressCategory(item)}
        activeOpacity={0.8}
        style={styles.tagContainer(index)}>
        <Text
          testID={tID(`Resources-Text-Tag${index}`)}
          variation={
            selectedTags.includes(item) ? 'Text1SemiBold' : 'Text1Regular'
          }
          color={colors.textMuted}>
          {item.name}
        </Text>
      </TouchableOpacity>
    );
  };

  const renderEmptyListView = () => {
    return (
      <View
        testID={tID('Resources-View-EmptyContainer')}
        style={styles.emptyListContainer}>
        <EmptyFeed testID={tID('Resources-Image-Empty')} />
      </View>
    );
  };

  const renderFooter = () => {
    if (pageCount > 1) {
      return (
        <View
          testID={tID('Resources-View-PageViewContainer')}
          style={styles.pageViewContainer}>
          <PageView
            pageCount={pageCount}
            currentIndex={currentPage}
            onPressPage={page => onChangePage(page)}
            onPressPrevious={() => onChangePage(currentPage - 1)}
            onPressNext={() => onChangePage(currentPage + 1)}
          />
        </View>
      );
    } else {
      return null;
    }
  };

  return (
    <SafeAreaView
      testID={tID('Resources-SafeAreaView-Main')}
      style={[styles.container, Platform.OS === 'web' && styles.webPadding]}>
      {Platform.OS !== 'web' &&
        (viewType === VIEW_TYPE_RESOURCES.Resources ? (
          <TabHeader
            title="Resources"
            onPressRight={() => {
              setViewType(VIEW_TYPE_RESOURCES.Favorites);
            }}
          />
        ) : (
          <View
            testID={tID('Resources-View-HeaderContainer')}
            style={[styles.marginHorizontal24, styles.marginTop16]}>
            <Header
              title="My Favorites"
              onPressLeftIcon={() => setViewType(VIEW_TYPE_RESOURCES.Resources)}
            />
          </View>
        ))}
      <View style={[styles.container, styles.marginHorizontal24]}>
        <View
          testID={tID('Resources-View-SearchContainer')}
          style={[
            styles.marginVertical16,
            Platform.OS === 'web' && styles.webSearchContainer,
          ]}>
          {Platform.OS === 'web' &&
            viewType === VIEW_TYPE_RESOURCES.Resources && (
              <IconButton
                onPress={() => {
                  setViewType(VIEW_TYPE_RESOURCES.Favorites);
                }}
                imageSize={32}
                leftImage={<HeartIcon />}
                tintColor={colors.favorite}
                buttonSize={54}
                style={CommonStyles.mr_32}
              />
            )}
          <SearchFilterView
            tags={resourceCategories}
            searchTerm={searchTerm}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            setSearchTerm={onChangeSearchTerm}
          />
        </View>
        {Platform.OS === 'web' ? (
          <>
            {renderHeaderComponent()}
            {viewType === VIEW_TYPE_RESOURCES.Favorites ? (
              <FlatGrid
                testID={tID('Resources-FlatGrid-All')}
                data={resources}
                renderItem={renderResourceItem}
                itemDimension={330}
                spacing={24}
                ListEmptyComponent={renderEmptyListView}
                style={styles.mhMinus24}
              />
            ) : resources?.length ? (
              <div testID={tID('Resources-div-List')} style={styles.cards}>
                {resources.map((item, index) =>
                  renderResourceItem({item, index}),
                )}
              </div>
            ) : (
              renderEmptyListView()
            )}
          </>
        ) : (
          <FlatList
            testID={tID('Resources-FlatList-All')}
            ref={flatlist}
            onRefresh={onRefreshList}
            refreshing={false}
            data={resources}
            renderItem={renderResourceItem}
            keyExtractor={(item, index) => index}
            showsVerticalScrollIndicator={false}
            ListHeaderComponent={renderHeaderComponent}
            ListEmptyComponent={renderEmptyListView}
            ListFooterComponent={renderFooter}
          />
        )}
      </View>
    </SafeAreaView>
  );
}

export default Resources;
